<script lang="ts">
export const elTagColors = [
  'primary',
  'rose',
  'pink',
  'purple',
  'indigo',
  'blue',
  'cyan',
  'gray',
  'teal',
  'green',
  'yellow',
  'orange',
  'red',
] as const;
export type ElTagColor = (typeof elTagColors)[number];

export const elTagSizes = ['xxs', 'xs'] as const;
export type ElTagSize = (typeof elTagSizes)[number];
</script>

<script setup lang="ts">
import { computed } from 'vue';
import ElIcon, { ElIconProps } from '@/ElIcon.vue';

const props = withDefaults(
  defineProps<{
    text: string;
    color?: ElTagColor;
    size?: ElTagSize;
    outlined?: boolean;
    leadingIcon?: ElIconProps;
    trailingIcon?: ElIconProps;
  }>(),
  {
    color: 'primary',
    size: 'xxs',
    outlined: false,
    leadingIcon: undefined,
    trailingIcon: undefined,
  },
);

const colorClasses = computed<string>(() => {
  switch (props.color) {
    case 'rose':
      return 'bg-rose-50 text-rose-700';
    case 'pink':
      return 'bg-pink-50 text-pink-700';
    case 'purple':
      return 'bg-purple-50 text-purple-700';
    case 'indigo':
      return 'bg-indigo-50 text-indigo-700';
    case 'blue':
      return 'bg-blue-50 text-blue-700';
    case 'cyan':
      return 'bg-cyan-50 text-cyan-700';
    case 'gray':
      return 'bg-gray-100 text-neutral-darker';
    case 'teal':
      return 'bg-teal-50 text-teal-700';
    case 'green':
      return 'bg-green-50 text-green-700';
    case 'yellow':
      return 'bg-yellow-50 text-yellow-700';
    case 'orange':
      return 'bg-orange-50 text-orange-700';
    case 'red':
      return 'bg-red-50 text-red-700';
    case 'primary':
    default:
      return 'bg-brandBlue-100 text-brandBlue-800';
  }
});

const colorOutlineClasses = computed(() => {
  switch (props.color) {
    case 'rose':
      return 'text-rose-500 border border-rose-500';
    case 'pink':
      return 'text-pink-500 border border-pink-500';
    case 'purple':
      return 'text-purple-600 border border-purple-600';
    case 'indigo':
      return 'text-indigo-500 border border-indigo-500';
    case 'blue':
      return 'text-blue-500 border border-blue-500';
    case 'cyan':
      return 'text-cyan-500 border border-cyan-500';
    case 'gray':
      return 'text-neutral-lighter border border-gray-500';
    case 'teal':
      return 'text-teal-500 border border-teal-500';
    case 'green':
      return 'text-green-500 border border-green-500';
    case 'yellow':
      return 'text-yellow-500 border border-yellow-500';
    case 'orange':
      return 'text-orange-500 border border-orange-500';
    case 'red':
      return 'text-red-500 border border-red-500';
    case 'primary':
    default:
      return 'text-brandBlue-800 border border-brandBlue-800';
  }
});
</script>

<template>
  <div
    class="inline-flex items-center gap-1 rounded-full"
    :class="[
      {
        'h-5 px-2': size === 'xxs',
        'h-6 px-3': size === 'xs',
      },
      outlined ? colorOutlineClasses : colorClasses,
    ]"
  >
    <ElIcon
      v-if="leadingIcon"
      :name="leadingIcon.name"
      :solid="leadingIcon.solid"
      :class="{ 'h-2 w-2': size === 'xxs', 'h-3 w-3': size === 'xs' }"
      disabled
    />

    <span v-if="text.length" class="text-xs">
      {{ text }}
    </span>
    <ElIcon
      v-if="trailingIcon"
      :name="trailingIcon.name"
      :solid="trailingIcon.solid"
      :class="{ 'h-2 w-2': size === 'xxs', 'h-3 w-3': size === 'xs' }"
      disabled
    />
  </div>
</template>
